import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { DeviceService } from './tv-detect.service';

/**
 * OverlayType - Types of overlays that can be displayed on TV
 */
export enum OverlayType {
  KEYBOARD = 'keyboard',
  MODAL = 'modal',
  DIALOG = 'dialog'
}

/**
 * Overlay - Represents an active overlay with a close callback
 */
export interface Overlay {
  id: string;
  type: OverlayType;
  onClose: () => void;
}

/**
 * TvBackButtonService - Centralized back button handling for TV platforms
 *
 * Implements PLAT-01 back button priority:
 * 1. Close topmost overlay (keyboard, modal, dialog)
 * 2. Navigate browser history back
 * 3. Exit app (no confirmation dialog)
 *
 * Listens for platform-specific back button keys:
 * - Escape (all platforms)
 * - Backspace (Android TV)
 * - XF86Back (Samsung Tizen)
 * - GoBack (LG webOS)
 *
 * Only active on TV platforms (gated by DeviceService.isTV())
 */
@Injectable({ providedIn: 'root' })
export class TvBackButtonService {
  /** Stack of active overlays (LIFO - last in, first out) */
  private overlayStack: Overlay[] = [];

  /** Observable overlay stack for reactive access */
  private overlayStack$ = new BehaviorSubject<Overlay[]>([]);

  /** Navigation depth counter (increments on NavigationEnd) */
  private navigationDepth = 0;

  /**
   * When popOverlay() calls history.back() to clean up the sentinel state,
   * the resulting popstate must NOT be interpreted as a native back press.
   * This flag gates the popstate handler for one event.
   */
  private suppressPopstate = false;

  constructor(
    private location: Location,
    private router: Router,
    private zone: NgZone,
    private deviceService: DeviceService
  ) {
    // Always register listener — the handler defensively checks isTV() at call time.
    // This avoids a timing bug where DeviceService hasn't detected TV yet at construction
    // (e.g. Android TV NativeBridge injects late), which would otherwise permanently
    // disable back-button handling for the session.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('keydown', this.handleBackButton.bind(this));
      // popstate catches native back-button presses from Android TV / Tizen / webOS
      // wrappers that dispatch history navigation directly without emitting a keydown.
      window.addEventListener('popstate', this.handlePopstate.bind(this));
    });

    // Track navigation depth for history-based back navigation
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.navigationDepth++;
      });
  }

  /**
   * Handle back button press with priority-based logic
   * Priority 1: Close topmost overlay
   * Priority 2: Navigate browser history back
   * Priority 3: Exit app (no confirmation)
   */
  private handleBackButton(event: KeyboardEvent): void {
    // Double-check TV platform (defensive)
    if (!this.deviceService.isTV()) {
      return;
    }

    // Only handle back button keys. 'Back' and 'BrowserBack' cover Android TV
    // wrappers that dispatch non-standard key names.
    const isBackKey = ['Escape', 'Backspace', 'XF86Back', 'GoBack', 'Back', 'BrowserBack'].includes(event.key);
    if (!isBackKey) {
      return;
    }

    console.log('[TVBB-KEY]', event.key, 'stack=', this.overlayStack.length);

    // Prevent default browser behavior
    event.preventDefault();
    event.stopPropagation();

    // Priority 1: Close topmost overlay
    if (this.overlayStack.length > 0) {
      const overlay = this.overlayStack[this.overlayStack.length - 1];
      this.popOverlay(overlay.id);
      try {
        this.zone.run(() => {
          overlay.onClose();
        });
      } catch (e) {
        console.error('Overlay onClose error:', e);
      }
      return;
    }

    // Priority 2: Navigate browser history back
    if (this.navigationDepth > 1) {
      this.zone.run(() => {
        this.location.back();
        this.navigationDepth--;
      });
      return;
    }

    // Priority 3: Exit app (no confirmation dialog per PLAT-01)
    this.exitApp();
  }

  /**
   * popstate handler — catches native back presses that don't emit a keydown.
   * If an overlay is on the stack when popstate fires, the user consumed our
   * sentinel history state; close the topmost overlay instead of letting the
   * navigation proceed.
   */
  private handlePopstate(_event: PopStateEvent): void {
    if (!this.deviceService.isTV()) return;
    if (this.suppressPopstate) {
      this.suppressPopstate = false;
      return;
    }
    console.log('[TVBB-POP] stack=', this.overlayStack.length);
    if (this.overlayStack.length === 0) return;

    const overlay = this.overlayStack[this.overlayStack.length - 1];
    // Direct stack mutation — don't call popOverlay() here, which would
    // trigger another history.back() and loop.
    this.overlayStack.pop();
    this.overlayStack$.next([...this.overlayStack]);
    try {
      this.zone.run(() => overlay.onClose());
    } catch (e) {
      console.error('Overlay onClose error (popstate):', e);
    }
  }

  /**
   * Push an overlay onto the stack
   * Newer overlays take priority (LIFO)
   *
   * On TV platforms, also pushes a sentinel history state so native back
   * buttons that navigate browser history (instead of emitting a keydown)
   * are captured via our popstate handler.
   */
  pushOverlay(overlay: Overlay): void {
    this.overlayStack.push(overlay);
    this.overlayStack$.next([...this.overlayStack]);

    if (this.deviceService.isTV()) {
      try {
        history.pushState({ tvOverlay: overlay.id }, '', location.href);
      } catch {
        // pushState can throw in sandboxed contexts — keydown path still works
      }
    }
  }

  /**
   * Remove an overlay from the stack by ID
   * Idempotent - no error if ID not found
   *
   * If the matching sentinel state is at the top of history, remove it via
   * history.back() (with suppress flag so we don't re-close another overlay).
   */
  popOverlay(id: string, skipHistoryBack: boolean = false): void {
    const index = this.overlayStack.findIndex(o => o.id === id);
    if (index === -1) return;

    this.overlayStack.splice(index, 1);
    this.overlayStack$.next([...this.overlayStack]);

    if (this.deviceService.isTV() && history.state?.tvOverlay === id && !skipHistoryBack) {
      this.suppressPopstate = true;
      try {
        history.back();
      } catch {
        this.suppressPopstate = false;
      }
    }
  }

  /**
   * Check if any overlays are currently active
   */
  hasOverlays(): boolean {
    return this.overlayStack.length > 0;
  }

  /**
   * Get observable of overlay stack for reactive components
   */
  getOverlayStack$(): Observable<Overlay[]> {
    return this.overlayStack$.asObservable();
  }

  /**
   * Exit the app using platform-specific methods
   * No confirmation dialog per PLAT-01
   */
  private exitApp(): void {
    // Samsung Tizen
    if ((window as any).tizen?.application) {
      const app = (window as any).tizen.application.getCurrentApplication();
      if (app?.exit) {
        app.exit();
        return;
      }
    }

    // LG webOS
    if ((window as any).webOS?.platformBack) {
      (window as any).webOS.platformBack();
      return;
    }

    // Android TV / fallback
    window.close();
  }
}
